import type { Product } from "@shopify/hydrogen/storefront-api-types";
import { useCallback, useEffect, useMemo, useState } from "react";
import { translate } from "@locales";

interface Header {
  key: string;
  label: string;
  selected: boolean;
}

function useProductDescription(product: Product) {
  const [header, setHeader] = useState<Header[]>([
    {
      key: "description",
      label: translate("shop.product.description"),
      selected: true,
    },
  ]);

  const isMarker = useMemo(
    () => product.tags.some((tag) => tag.toLowerCase().includes("marker")),
    [product]
  ); // TODO: filter by collection

  useEffect(() => {
    const hasAcmiApproved = Boolean(
      header.find(
        (el) => el.label === translate("shop.product.acmi-approved.header")
      )
    );
    if (!hasAcmiApproved && isMarker)
      setHeader([
        ...header,
        {
          key: "acmi approved",
          label: translate("shop.product.acmi-approved.header"),
          selected: false,
        },
      ]);
  }, [isMarker]);

  const selectHeader = useCallback(
    (key: number) => {
      const temp: typeof header = structuredClone(header);
      temp.forEach((el) => (el.selected = false));
      temp[key].selected = true;
      setHeader(temp);
    },
    [header]
  );

  const warningText = useMemo(() => {
    const message = product.metafields[1]?.value ?? "";
    const index = message.indexOf(":");
    return message.substring(index + 1).trim();
  }, [product]);

  return {
    models: {
      header,
      warningText,
    },
    operations: {
      selectHeader,
    },
  };
}

interface Props {
  product: Product;
}

export function ProductDescription({ product }: Props) {
  const { models, operations } = useProductDescription(product);
  const { selectHeader } = operations;
  const { header } = models;

  const buttonsHTML = header.map(({ label, selected }, i) => {
    const baseClass =
      "py-2 px-4 uppercase font-bold border-solid border-gray-200 whitespace-nowrap h-12";
    const selectedClass = "border-t border-x italic";
    const unselectedClass = "text-black/50 border-b";
    const className = `${baseClass} ${
      selected ? selectedClass : unselectedClass
    }`;

    return (
      <button key={i} className={className} onClick={() => selectHeader(i)}>
        {label}
      </button>
    );
  });

  return (
    <div className="mt-20 flex flex-col items-center w-full">
      <div className="w-full flex justify-center">
        <div className="w-full border-b border-solid border-gray-200" />
        {buttonsHTML}
        <div className="w-full border-b border-solid border-gray-200" />
      </div>
      <ContentRender product={product} header={header} />
    </div>
  );
}

interface ContentRenderProps {
  product: Product;
  header: Header[];
}

function ContentRender({ product, header }: ContentRenderProps) {
  const { models } = useProductDescription(product);
  if (header.find((el) => el.key === "description")?.selected) {
    return (
      <div>
        <div
          id="product-description"
          className="prose pt-16 text-black text-md"
          dangerouslySetInnerHTML={{ __html: product.descriptionHtml }}
        />
        {product.metafields[1]?.value && (
          <div className="flex flex-col items-center">
            <div className="mt-6 mb-4 text-xs font-chivo-regular justify-center">
              <span>{models.warningText ?? ""}</span>
            </div>
            <div className="flex justify-center mb-10">
              <img
                className="h-[42px] w-8 mr-4 object-contain"
                src="/static/img/acmi-ap-logo.png"
                alt="acmi ap logo"
              />
            </div>
          </div>
        )}
      </div>
    );
  }

  if (header.find((el) => el.key === "acmi approved")?.selected) {
    return (
      <div className="flex prose pt-16 text-black text-md lg:flex-row flex-col pb-10">
        <img
          className="object-contain self-center"
          width={250}
          src="//cdn.shopify.com/s/files/1/0044/3708/2200/t/10/assets/bg-slide-markers-2.png?v=168235169990744382881571927433"
          loading="lazy"
        />
        <div className="info">
          <h5 className="uppercase text-sm">
            {translate("shop.product.acmi-approved.content.title")}
          </h5>
          <p className="text-sm leading-[26px]">
            {translate("shop.product.acmi-approved.content.body")}
          </p>
          <a
            href="https://www.acmiart.org/"
            target="_blank"
            className="text-xs text-[#2991C9] uppercase"
          >
            {translate("shop.product.acmi-approved.content.learn-more")}
          </a>
        </div>
      </div>
    );
  }

  return null;
}
